import { observer } from 'mobx-react'
import React from 'react'

import {
   GetEntityJsonConfigurationDocument,
   UserPermittedTemplateAccessOverrideConfig
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { JsonConfigurationEntityType } from '@flowwai-frontend/bps-officer-web/graphql'
import {
   useIsDashboardAdmin,
   useUserAppConfigStore
} from '@flowwai-frontend/bps-officer-web/iam'
import {
   UnionNavItemModel,
   useNavbarStore
} from '@flowwai-frontend/bps-officer-web/navbar-base'
import { useApolloClientWithAuth } from '@flowwai-frontend/shared/auth/common'
import { EMPTY_STRING } from '@flowwai-frontend/shared/constants'

import NavbarTemplatesSection from '../../components/NavbarTemplatesSection/NavbarTemplatesSection'

const TDR_DIGITALIZATION_NAV_ITEM_SUFFIX = '-tdr digitalization'

// TDR digitalization nav items are derived from a base template and carry a
// "-tdr digitalization" suffix on their id; access is granted against the
// base templateId, so strip the suffix before checking the access config.
const resolveTemplateIdForAccessCheck = (navItemId: string): string =>
   navItemId.endsWith(TDR_DIGITALIZATION_NAV_ITEM_SUFFIX)
      ? navItemId.slice(0, -TDR_DIGITALIZATION_NAV_ITEM_SUFFIX.length)
      : navItemId

const TemplatesSectionNavItemController = (): React.ReactElement | null => {
   const navbarStore = useNavbarStore()
   const isDashboardAdmin = useIsDashboardAdmin()
   const { userId } = useUserAppConfigStore()

   const client = useApolloClientWithAuth()

   // Hide templates section for dashboard admin users
   if (isDashboardAdmin) {
      return null
   }

   const getTemplateNavItems = (): UnionNavItemModel[] | undefined => {
      try {
         const entityJsonConfigResponse = client.readQuery({
            query: GetEntityJsonConfigurationDocument,
            variables: {
               params: {
                  entityType:
                     JsonConfigurationEntityType.PipelineItemTemplateAccessOverrideConfig
               }
            }
         })

         const mappingData = JSON.parse(
            entityJsonConfigResponse?.getEntityJsonConfiguration
               ?.jsonConfiguration?.jsonData || ''
         ) as UserPermittedTemplateAccessOverrideConfig | null

         const currentUserIdTemplatesConfig =
            mappingData?.user_permitted_pipeline_item_templates_map?.[
               userId ?? EMPTY_STRING
            ]

         const hasTemplatesConfigForUser = currentUserIdTemplatesConfig?.length

         if (!hasTemplatesConfigForUser) {
            return navbarStore.templatesNavItem?.items
         }

         return navbarStore.templatesNavItem?.items?.filter(eachItem =>
            currentUserIdTemplatesConfig.includes(
               resolveTemplateIdForAccessCheck(eachItem.id)
            )
         )
      } catch (error) {
         return navbarStore.templatesNavItem?.items
      }
   }

   const updatedTemplateNavItems = getTemplateNavItems()

   const hasTemplateNavItems = !!updatedTemplateNavItems?.length

   return hasTemplateNavItems ? (
      <>
         <NavbarTemplatesSection templateNavItems={updatedTemplateNavItems} />
         <hr className='my-[8px]' />
      </>
   ) : null
}

export default observer(TemplatesSectionNavItemController)
